/* eslint-disable import/order */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable max-len */
import {
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  UploadOutlined
} from '@ant-design/icons';
import {
  Form,
  Input,
  Checkbox,
  Radio,
  Select,
  TimePicker,
  Upload,
  Button,
  Space,
  Table,
  Tooltip,
  Col,
  Row,
  UploadProps
} from 'antd';
import {useWatch} from 'antd/es/form/Form';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import moment from 'moment';
import React, {useContext, useEffect, useState} from 'react';
import ReactQuill from 'react-quill';
import {useParams, useSearchParams} from 'react-router-dom';

import 'react-quill/dist/quill.snow.css';
import PhoneNumberInput from '../../../components/CustomInputs/PhoneNumberInput/PhoneNumberInput';
import MomentDatePicker from '../../../components/DatePicker/MomentDatePicker';
import {filterSelectOptions} from '../../../components/Forms/Utils/commonFunctions';
import {evaluateExpression} from '../../../components/Forms/Utils/expressionEvaluator';
import QuestionnaireTable from '../../../components/Table/QuestionnaireTable';
import QuestionnaireTableWithFixedRow from '../../../components/Table/TableWithfixedRow';
import QuestionnaireTableWithYearFixedRow from '../../../components/Table/TableWithYearFixedRow';
import {CommonErrors} from '../../../constants/Common';
import {replaceCurrentAndPrevious} from '../../../shared/checkSelectedYear';
import NumberInput from '../../../shared/NumberInput';
import {headerLabelContainer} from './headerLabelContainer';
import uploadDocumentAPI from '../../../services/modules/ESG/uploadDocumentAPI';
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';
import useJSONStore from '../../../store/StaticJson/jsonStore';
import useUserStore from '../../../store/userStore/userStore';
import {convertPxToRem} from '../../../utils/commonFunctions';
import RegEx from '../../../utils/FormUtils/RegEx';
import showMessage from '../../../utils/showMessage';
import {sortedOptions} from '../constants/commonFunctions';
import {
  isFieldVisible,
  normalizeTableValue,
  isFieldDisabled,
  isFieldRequired
} from '../constants/commonInputFormFunction';
import SubmitDisabledContext from '../constants/SubmitDisabledContext';
import {IField, inputType, conditionType} from '../interface/inputType';

import DisplayCalculations from './DisplayCalculations';
import {
  getFinancialYearDates,
  getFinancialYearDisabledDate,
  isDateWithinFinancialYear
} from './financialYearDateValidation';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import InputGrid from './InputGrid';
import PromptSteps from './PromptSteps';
import QuestionnaireAddMoreFieldsSwitch from './QuestionnarieAddMoreFieldsSwitch';
import WysiwygInput from './WysiwygInput';

const {TextArea} = Input;
const {RangePicker} = MomentDatePicker;

const {Column, ColumnGroup} = Table;

type FieldStates = Record<string, string | undefined>;

// Resolves the value of `dependsOn`. When `relatedTableKey` points to a table,
// the dependent key can live on any row (e.g. `_Scope2` / `_Scope3` rows of a
// fixed-row table), so search all rows instead of only the first one.
const getDependsOnValue = (field: any, getFV: (name: any) => any): any => {
  if (!field?.relatedTableKey) return getFV(field?.dependsOn);

  const dependsOnTableKeyValue =
    normalizeTableValue(getFV(field.relatedTableKey)) ?? [];
  const tableData = Array.isArray(dependsOnTableKeyValue)
    ? dependsOnTableKeyValue
    : [dependsOnTableKeyValue];

  if (Array.isArray(field?.dependsOn)) {
    return field.dependsOn.flatMap((key: string) =>
      tableData
        .filter(
          (row: any) =>
            row !== null && typeof row === 'object' && row[key] !== undefined
        )
        .flatMap((row: any) => row[key])
    );
  }

  const dependsOnKey = field?.dependsOn ?? '';

  return tableData.find(
    (row: any) =>
      row !== null && typeof row === 'object' && row[dependsOnKey] !== undefined
  )?.[dependsOnKey];
};

const matchesShowWhen = (field: any, value: any): boolean => {
  const showWhenArray = Array.isArray(field?.showWhen)
    ? field.showWhen
    : [field?.showWhen];

  if (Array.isArray(value)) {
    return value.some((val: any) => showWhenArray.includes(val));
  }

  return value !== undefined && value !== null && showWhenArray.includes(value);
};

const QuestionnaireSwitch: React.FC<{
  field: IField;
}> = ({field}) => {
  const displayFieldLabel = field.questionNo
    ? `${field.questionNo} ${field.fieldLabel}`
    : field.fieldLabel;

  const [searchParams] = useSearchParams();
  const selectedYear = searchParams.get('selectedYear');
  const [dataSource, setDataSource] = useState(field.fieldStructure);
  const [showTextBox, setShowTextBox] = useState<FieldStates>({});
  const [glaoQuestion40To48showTextBox, setGlaoQuestion40To48showTextBox] =
    useState<FieldStates>({});
  const [ghgEmissionReportingQuestion10, setGHGEmissionReportingQuestion10] =
    useState(false);
  const [radioGroupIfYesThenShowTextArea, setRadioGroupIfYesThenShowTextArea] =
    useState<FieldStates>({});
  const [radioGroupIfYesThenShowTextBox, setRadioGroupIfYesThenShowTextBox] =
    useState<FieldStates>({});
  const [radioGroupIfNoThenShowTextBox, setRadioGroupIfNoThenShowTextBox] =
    useState<FieldStates>({});
  const [checkboxNAThenTextarea, setCheckboxNAThenTextarea] = useState(false);

  const FormInstance = useFormInstance();
  const {getFieldValue, setFieldValue} = FormInstance;
  const {framework} = useParams();

  // Re-render as soon as the field controlling conditional visibility changes
  // (e.g. when saved row data is loaded into a modal form), so dependent fields
  // show/hide immediately instead of waiting for an unrelated re-render
  const visibilityDependsOnField =
    field?.conditionalVisibility?.relatedTableKey ??
    field?.conditionalVisibility?.dependsOn;
  useWatch(
    (values: any) =>
      visibilityDependsOnField ? values?.[visibilityDependsOnField] : undefined,
    {form: FormInstance, preserve: true}
  );

  const prevDisabledStateFieldName = `__prevDisabled_${field.fieldName}`;

  const dependentFieldName = field.disabledOn?.fieldName;
  const watchFieldName = dependentFieldName || field.fieldName;
  const dependentValue = useWatch(watchFieldName, FormInstance);

  const transformNestedOptions = (options: any[]): any[] => {
    if (!options || !Array.isArray(options)) {
      return [];
    }

    return options.map((opt: any) => {
      // If option has nested options, create a group
      if (opt.options && Array.isArray(opt.options) && opt.options.length > 0) {
        return {
          label: opt.label,
          options: transformNestedOptions(opt.options)
        };
      }

      return {
        value: opt.value,
        label: opt.label
      };
    });
  };

  useEffect(() => {
    if (!field.disabledOn || !dependentFieldName) {
      if (!field.disabledOn) {
        const prevState = getFieldValue(prevDisabledStateFieldName);
        if (prevState !== null && prevState !== undefined) {
          setFieldValue(prevDisabledStateFieldName, undefined);
        }
      }

      return;
    }

    const isCurrentlyDisabled = isFieldDisabled(
      field,
      getFieldValue,
      (field as any).rowId
    );

    const prevDisabledState = getFieldValue(prevDisabledStateFieldName);

    // Check if field transitioned from enabled to disabled
    if (prevDisabledState === false && isCurrentlyDisabled) {
      // Field was enabled before but is now disabled - clear its value
      const currentValue = getFieldValue(field.fieldName);
      if (
        currentValue !== undefined &&
        currentValue !== null &&
        currentValue !== ''
      ) {
        setFieldValue(field.fieldName, undefined);
        setSubmitDisabled?.(false); // Enable submit button as form state changed
      }
    }

    // Update previous disabled state in form field
    setFieldValue(prevDisabledStateFieldName, isCurrentlyDisabled);
  }, [
    dependentValue,
    field.fieldName,
    field.disabledOn,
    dependentFieldName,
    prevDisabledStateFieldName
  ]);

  const countryOptions = useJSONStore((state) => state.country);
  const stateOptions = useJSONStore((state) => state.states);
  const cityOptions = useJSONStore((state) => state.city);
  const companyId = useCompanyStore.getState().companyId;
  const userId = useUserStore((state) => state.userDetails._id);
  const setSubmitDisabled = useContext(SubmitDisabledContext);

  const {
    HEADING,
    HEADING_2,
    HEADING_3,
    HEADING_5,
    TEXT,
    WYSIWYG,
    CHECKBOX,
    CHECKBOX_GLAO_QUESTION_40_TO_48,
    CHECKBOX_NA_THEN_TEXTAREA,
    CHECKBOXGROUP,
    DATEPICKER,
    DATEPICKER_FINANCIAL_YEAR,
    DATE_RANGE_PICKER,
    FILEUPLOAD,
    MULTISELECT,
    NUMBER,
    PERCENTAGE,
    RADIOGROUP,
    GHG_EMISSIONS_REPORTING_10,
    RADIOGROUP_YES_THEN_SHOW_TABLE,
    RADIOGROUP_NO_THEN_SHOW_TABLE,
    RADIOGROUP_IF_YES_THEN_TEXTAREA,
    RADIOGROUP_IF_YES_THEN_TEXTBOX,
    RADIOGROUP_IF_NO_THEN_TEXTBOX,
    SELECT,
    CITY,
    STATE,
    COUNTRY,
    TEXTAREA,
    TIMEPICKER,
    ADD_MORE_FIELDS,
    ADD_MORE_FIELDS_TOGGLE_TABLE,
    TABLE,
    INPUT_GRID,
    RADIOGROUP_IF_YES_ADDMORE_TEXTAREA,
    ADD_NEW_TO_TABLE,
    ADDMORE_TEXTAREA,
    ADD_FIXED_ROW_TO_TABLE,
    ADD_YEAR_FIXED_ROW_TO_TABLE,
    SELECT_WITH_OTHER_TEXTBOX,
    MULTISELECT_WITH_OTHER_TEXTBOX
  } = inputType;

  const handleAdd = () => {
    const newData = field.addMoreOption.obj;
    setDataSource([...dataSource, newData]);
  };

  const handleOnChange = (fieldName: any, value: any) => {
    const shouldShowTextBox =
      value === 'Other' ||
      value === 'Other, please specify' ||
      (typeof value === 'string' && value.startsWith('Other ')) ||
      value.endsWith('please specify');
    setShowTextBox({
      ...showTextBox,
      [fieldName]: shouldShowTextBox
    });
  };

  const handleMultiSelectOnChange = (fieldName: any, value: any) => {
    // For multiselect, value is an array
    const isArray = Array.isArray(value);
    const shouldShowTextBox =
      isArray &&
      value?.length > 0 &&
      (value.includes('Other') ||
        value.includes('Other, please specify') ||
        value.some(
          (item: string) =>
            typeof item === 'string' &&
            item?.startsWith('Other ') &&
            item?.endsWith('please specify')
        ));
    setShowTextBox({
      ...showTextBox,
      [fieldName]: shouldShowTextBox
    });
  };

  const handleOnChange2 = (fieldName: any, value: any) => {
    setGlaoQuestion40To48showTextBox({
      ...glaoQuestion40To48showTextBox,
      [fieldName]: value
    });
  };

  const handleOnChangeGHGEmission = (e: any) => {
    setGHGEmissionReportingQuestion10(e.target.value === 'Yes');
  };

  const handleOnChange10 = (fieldName: any, value: any) => {
    setRadioGroupIfYesThenShowTextBox({
      ...radioGroupIfYesThenShowTextBox,
      [fieldName]: value === 'Yes'
    });
  };

  const handleOnChange11 = (fieldName: any, value: any) => {
    setRadioGroupIfNoThenShowTextBox({
      ...radioGroupIfNoThenShowTextBox,
      [fieldName]: value === 'No'
    });
  };

  const handleOnChange13 = (e: any) => {
    setCheckboxNAThenTextarea(e.target.checked);
  };

  const handleOnChange14 = (fieldName: any, value: any) => {
    setRadioGroupIfYesThenShowTextArea({
      ...radioGroupIfYesThenShowTextArea,
      [fieldName]: value === 'Yes'
    });
  };

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const blankObj: any = {};

  // Check if field should be visible based on conditional visibility
  if (
    !isFieldVisible(field, getFieldValue) ||
    field?.validationData?.[field.fieldName]?.active === false
  ) {
    return null;
  }

  const headingComponent = (headingType: inputType, refField?: any) => {
    const fieldData = refField ?? field;
    const headerContainer = (comp: any): any => {
      switch (headingType) {
        case inputType.HEADING:
          return <h4>{comp}</h4>;
        case inputType.HEADING_1:
          return <h1>{comp}</h1>;
        case inputType.HEADING_2:
          return <h2>{comp}</h2>;
        case inputType.HEADING_3:
          return <h3>{comp}</h3>;
        case inputType.HEADING_4:
          return <h4>{comp}</h4>;
        case inputType.HEADING_5:
          return <h5>{comp}</h5>;
        default:
          <h4>{comp}</h4>;
      }
    };
    const replaceHeading = replaceCurrentAndPrevious(
      fieldData?.heading ?? '',
      selectedYear ?? ''
    );

    return headerContainer(
      <>
        {replaceHeading}
        {fieldData.tooltip ? (
          <Tooltip
            title={fieldData.tooltip}
            autoAdjustOverflow={true}
            arrow={false}
            overlayClassName="esg-tooltip"
            // overlayStyle={tooltipCSS}
          >
            <ExclamationCircleOutlined className="iButtonIcon" />
          </Tooltip>
        ) : (
          ''
        )}
      </>
    );
  };

  switch (field.fieldType) {
    case HEADING_5:
      return headingComponent(inputType.HEADING_5);
    case HEADING:
      if (field?.visibilityDependsOnField) {
        return (
          <Form.Item shouldUpdate noStyle>
            {({getFieldValue}) => {
              const depValue = getFieldValue(field?.visibilityDependsOnField);
              const shouldVisible =
                depValue !== undefined && depValue !== false;

              return shouldVisible ? (
                headingComponent(inputType.HEADING)
              ) : (
                <></>
              );
            }}
          </Form.Item>
        );
      }
      // conditional visibility
      if (field?.conditionalVisibility) {
        return (
          <Form.Item
            shouldUpdate
            noStyle
            dependencies={[field.conditionalVisibility.dependsOn]}
          >
            {({getFieldValue}) => {
              const shouldShow = isFieldVisible(field, getFieldValue);
              if (!shouldShow) return null;

              return headingComponent(inputType.HEADING);
            }}
          </Form.Item>
        );
      }

      return headingComponent(inputType.HEADING);
    case HEADING_3:
      return headingComponent(inputType.HEADING_3);
    case HEADING_2:
      return headingComponent(inputType.HEADING_2);
    case TEXT:
      const isTextRequired = isFieldRequired(field, getFieldValue);
      const textDependencyField = field.requiredOn?.fieldName;

      return textDependencyField ? (
        <Form.Item shouldUpdate noStyle dependencies={[textDependencyField]}>
          {({getFieldValue: getFieldValueDynamic}) => {
            const isTextRequiredDynamic = isFieldRequired(
              field,
              getFieldValueDynamic
            );

            return (
              <Form.Item
                name={field.fieldName}
                label={displayFieldLabel}
                tooltip={
                  field.tooltip
                    ? {
                        title: field.tooltip,
                        icon: <InfoCircleOutlined />
                        // overlayStyle: tooltipCSS
                      }
                    : ''
                }
                className={field?.colSpan ? '' : 'col-span-center'}
                labelCol={field?.colSpan ? {span: 24} : undefined}
                rules={
                  isTextRequiredDynamic
                    ? [
                        {
                          required: true,
                          message: `${field.fieldLabel} is required`,
                          validator: async (_, value) => {
                            if (
                              !value ||
                              (typeof value === 'string' && value.length === 0)
                            ) {
                              return await Promise.reject(
                                new Error(`${field.fieldLabel} is required`)
                              );
                            }

                            await Promise.resolve();
                          }
                        }
                      ]
                    : []
                }
              >
                <Input
                  name={field.fieldName}
                  maxLength={field.textLimit}
                  disabled={
                    field.disabled ||
                    isFieldDisabled(
                      field,
                      getFieldValueDynamic,
                      (field as any).rowId
                    )
                  }
                  placeholder={field.placeHolder}
                  onInput={(e) =>
                    field.convertToUpperCase
                      ? ((e.target as HTMLInputElement).value = (
                          e.target as HTMLInputElement
                        ).value.toUpperCase())
                      : ''
                  }
                  style={{
                    width:
                      field.smallText === true ? convertPxToRem(250) : '100%'
                  }}
                />
              </Form.Item>
            );
          }}
        </Form.Item>
      ) : (
        <Form.Item
          name={field.fieldName}
          label={displayFieldLabel}
          initialValue={
            field.defaultValue !== undefined ? field.defaultValue : undefined
          }
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />
                  // overlayStyle: tooltipCSS
                }
              : ''
          }
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
          rules={
            isTextRequired
              ? [
                  {
                    required: true,
                    message: `${field.fieldLabel} is required`,
                    validator: async (_, value) => {
                      if (
                        !value ||
                        (typeof value === 'string' && value.length === 0)
                      ) {
                        return await Promise.reject(
                          new Error(`${field.fieldLabel} is required`)
                        );
                      }

                      await Promise.resolve();
                    }
                  }
                ]
              : []
          }
        >
          <Input
            name={field.fieldName}
            maxLength={field.textLimit}
            disabled={
              field.disabled ||
              isFieldDisabled(field, getFieldValue, (field as any).rowId)
            }
            placeholder={field.placeHolder}
            onInput={(e) =>
              field.convertToUpperCase
                ? ((e.target as HTMLInputElement).value = (
                    e.target as HTMLInputElement
                  ).value.toUpperCase())
                : ''
            }
            style={{
              width: field.smallText === true ? convertPxToRem(250) : '100%'
            }}
          />
        </Form.Item>
      );
    case inputType.EMAIL:
      const isEmailRequired = isFieldRequired(field, getFieldValue);
      const emailLabel = field.fieldLabel || 'Email';

      return (
        <Form.Item
          name={field.fieldName}
          label={displayFieldLabel}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />
                }
              : ''
          }
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
          rules={
            isEmailRequired
              ? [
                  {
                    required: true,
                    message: `${emailLabel} is required`,
                    validator: async (_, value) => {
                      if (
                        !value ||
                        (typeof value === 'string' && value.length === 0)
                      ) {
                        return await Promise.reject(
                          new Error(`${emailLabel} is required`)
                        );
                      }

                      if (
                        typeof value === 'string' &&
                        !RegEx.email.test(value)
                      ) {
                        return await Promise.reject(
                          new Error(CommonErrors.validField(emailLabel))
                        );
                      }

                      await Promise.resolve();
                    }
                  }
                ]
              : [
                  {
                    validator: async (_, value) => {
                      if (
                        value &&
                        typeof value === 'string' &&
                        !RegEx.email.test(value)
                      ) {
                        return await Promise.reject(
                          new Error(CommonErrors.validField(emailLabel))
                        );
                      }

                      await Promise.resolve();
                    }
                  }
                ]
          }
        >
          <Input
            name={field.fieldName}
            disabled={
              field.disabled ||
              isFieldDisabled(field, getFieldValue, (field as any).rowId)
            }
            placeholder={field.placeHolder}
            style={{
              width: field.smallText === true ? convertPxToRem(250) : '100%'
            }}
          />
        </Form.Item>
      );
    case inputType.MOBILE:
      const isMobileRequired = isFieldRequired(field, getFieldValue);
      const mobileLabel = field.fieldLabel || 'Mobile Number';

      return (
        <Form.Item
          name={field.fieldName}
          label={displayFieldLabel}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />
                }
              : ''
          }
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
          rules={
            isMobileRequired
              ? [
                  {
                    required: true,
                    message: `${mobileLabel} is required`,
                    validator: async (_, value) => {
                      if (
                        !value ||
                        (typeof value === 'string' && value.length === 0)
                      ) {
                        return await Promise.reject(
                          new Error(`${mobileLabel} is required`)
                        );
                      }

                      if (
                        typeof value === 'string' &&
                        !RegEx.telephone.test(value)
                      ) {
                        return await Promise.reject(
                          new Error(CommonErrors.validField(mobileLabel))
                        );
                      }

                      await Promise.resolve();
                    }
                  }
                ]
              : [
                  {
                    validator: async (_, value) => {
                      if (
                        value &&
                        typeof value === 'string' &&
                        !RegEx.telephone.test(value)
                      ) {
                        return await Promise.reject(
                          new Error(CommonErrors.validField(mobileLabel))
                        );
                      }

                      await Promise.resolve();
                    }
                  }
                ]
          }
        >
          <PhoneNumberInput disabled={field.disabled} />
        </Form.Item>
      );
    case inputType.ALPHANUMERIC:
      const isAlphaRequired = isFieldRequired(field, getFieldValue);
      const alphaDependencyField = field.requiredOn?.fieldName;

      return alphaDependencyField ? (
        <Form.Item shouldUpdate noStyle dependencies={[alphaDependencyField]}>
          {({getFieldValue: getFieldValueDynamic}) => {
            const isAlphaRequiredDynamic = isFieldRequired(
              field,
              getFieldValueDynamic
            );

            return (
              <Form.Item
                name={field.fieldName}
                label={displayFieldLabel}
                tooltip={
                  field.tooltip
                    ? {
                        title: field.tooltip,
                        icon: <InfoCircleOutlined />
                      }
                    : ''
                }
                className={field?.colSpan ? '' : 'col-span-center'}
                labelCol={field?.colSpan ? {span: 24} : undefined}
                rules={
                  isAlphaRequiredDynamic
                    ? [
                        {
                          required: true,
                          message: `${field.fieldLabel} is required`,
                          validator: async (_, value) => {
                            if (
                              !value ||
                              (typeof value === 'string' && value.length === 0)
                            ) {
                              return await Promise.reject(
                                new Error(`${field.fieldLabel} is required`)
                              );
                            }

                            if (
                              typeof value === 'string' &&
                              !RegEx.alphaNumeric.test(value)
                            ) {
                              return await Promise.reject(
                                new Error('No special Characters are allowed')
                              );
                            }

                            await Promise.resolve();
                          }
                        }
                      ]
                    : [
                        {
                          validator: async (_, value) => {
                            if (
                              value &&
                              typeof value === 'string' &&
                              !RegEx.alphaNumeric.test(value)
                            ) {
                              return await Promise.reject(
                                new Error('No special Characters are allowed')
                              );
                            }

                            await Promise.resolve();
                          }
                        }
                      ]
                }
              >
                <Input
                  name={field.fieldName}
                  disabled={
                    field.disabled ||
                    isFieldDisabled(
                      field,
                      getFieldValueDynamic,
                      (field as any).rowId
                    )
                  }
                  placeholder={field.placeHolder}
                  onInput={(e) =>
                    field.convertToUpperCase
                      ? ((e.target as HTMLInputElement).value = (
                          e.target as HTMLInputElement
                        ).value.toUpperCase())
                      : ''
                  }
                  style={{
                    width:
                      field.smallText === true ? convertPxToRem(250) : '100%'
                  }}
                />
              </Form.Item>
            );
          }}
        </Form.Item>
      ) : (
        <Form.Item
          name={field.fieldName}
          label={displayFieldLabel}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />
                }
              : ''
          }
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
          rules={
            isAlphaRequired
              ? [
                  {
                    required: true,
                    message: `${field.fieldLabel} is required`,
                    validator: async (_, value) => {
                      if (
                        !value ||
                        (typeof value === 'string' && value.length === 0)
                      ) {
                        return await Promise.reject(
                          new Error(`${field.fieldLabel} is required`)
                        );
                      }

                      if (
                        typeof value === 'string' &&
                        !RegEx.alphaNumeric.test(value)
                      ) {
                        return await Promise.reject(
                          new Error('No special Characters are allowed')
                        );
                      }

                      await Promise.resolve();
                    }
                  }
                ]
              : [
                  {
                    validator: async (_, value) => {
                      if (
                        value &&
                        typeof value === 'string' &&
                        !RegEx.alphaNumeric.test(value)
                      ) {
                        return await Promise.reject(
                          new Error('No special Characters are allowed')
                        );
                      }

                      await Promise.resolve();
                    }
                  }
                ]
          }
        >
          <Input
            name={field.fieldName}
            disabled={
              field.disabled ||
              isFieldDisabled(field, getFieldValue, (field as any).rowId)
            }
            placeholder={field.placeHolder}
            onInput={(e) =>
              field.convertToUpperCase
                ? ((e.target as HTMLInputElement).value = (
                    e.target as HTMLInputElement
                  ).value.toUpperCase())
                : ''
            }
            style={{
              width: field.smallText === true ? convertPxToRem(250) : '100%'
            }}
          />
        </Form.Item>
      );
    case TEXTAREA:
      const isTextareaRequired = isFieldRequired(field, getFieldValue);
      const textareaDependencyField = field.requiredOn?.fieldName;

      return textareaDependencyField ? (
        <Form.Item
          shouldUpdate
          noStyle
          dependencies={[textareaDependencyField]}
        >
          {({getFieldValue: getFieldValueDynamic}) => {
            const isTextareaRequiredDynamic = isFieldRequired(
              field,
              getFieldValueDynamic
            );

            return (
              <>
                <Form.Item
                  name={field.fieldName}
                  label={headerLabelContainer(
                    field?.heading as any,
                    displayFieldLabel
                  )}
                  tooltip={
                    field.tooltip
                      ? {
                          title: field.tooltip,
                          icon: <InfoCircleOutlined />
                          // overlayStyle: tooltipCSS
                        }
                      : ''
                  }
                  className={field?.colSpan ? '' : 'col-span-center'}
                  labelCol={field?.colSpan ? {span: 24} : undefined}
                  style={field.askAIkey ? {marginBottom: '0px'} : {}}
                  rules={
                    isTextareaRequiredDynamic
                      ? [
                          {
                            required: true,
                            message: `${field.fieldLabel} is required`,
                            validator: async (_, value) => {
                              if (
                                !value ||
                                (typeof value === 'string' &&
                                  value.length === 0)
                              ) {
                                return await Promise.reject(
                                  new Error(`${field.fieldLabel} is required`)
                                );
                              }

                              await Promise.resolve();
                            }
                          }
                        ]
                      : []
                  }
                >
                  {field.askAIkey ? (
                    <ReactQuill
                      placeholder={field.placeHolder}
                      readOnly={field.disabled}
                      style={{width: '100%'}}
                    />
                  ) : (
                    <TextArea
                      autoSize={{minRows: 4, maxRows: 20}}
                      disabled={field.disabled}
                      placeholder={field.placeHolder}
                    />
                  )}
                </Form.Item>

                {field.askAIkey && (
                  <Form.Item style={{marginBottom: '24px'}}>
                    <PromptSteps
                      field={field}
                      getFieldValue={getFieldValue}
                      setFieldValue={setFieldValue}
                      setSubmitDisabled={setSubmitDisabled}
                      userId={userId}
                      companyId={companyId}
                    />
                  </Form.Item>
                )}
              </>
            );
          }}
        </Form.Item>
      ) : (
        <>
          <Form.Item
            name={field.fieldName}
            label={headerLabelContainer(
              field?.heading as any,
              displayFieldLabel
            )}
            tooltip={
              field.tooltip
                ? {
                    title: field.tooltip,
                    icon: <InfoCircleOutlined />
                    // overlayStyle: tooltipCSS
                  }
                : ''
            }
            className={field?.colSpan ? '' : 'col-span-center'}
            labelCol={field?.colSpan ? {span: 24} : undefined}
            style={field.askAIkey ? {marginBottom: '0px'} : {}}
            rules={
              isTextareaRequired
                ? [
                    {
                      required: true,
                      message: `${field.fieldLabel} is required`,
                      validator: async (_, value) => {
                        if (
                          !value ||
                          (typeof value === 'string' && value.length === 0)
                        ) {
                          return await Promise.reject(
                            new Error(`${field.fieldLabel} is required`)
                          );
                        }

                        await Promise.resolve();
                      }
                    }
                  ]
                : []
            }
          >
            {field.askAIkey ? (
              <ReactQuill
                placeholder={field.placeHolder}
                readOnly={field.disabled}
                style={{width: '100%'}}
              />
            ) : (
              <TextArea
                autoSize={{minRows: 4, maxRows: 20}}
                disabled={field.disabled}
                placeholder={field.placeHolder}
              />
            )}
          </Form.Item>

          {field.askAIkey && (
            <Form.Item style={{marginBottom: '24px'}}>
              <PromptSteps
                field={field}
                getFieldValue={getFieldValue}
                setFieldValue={setFieldValue}
                setSubmitDisabled={setSubmitDisabled}
                userId={userId}
                companyId={companyId}
              />
            </Form.Item>
          )}
        </>
      );
    case SELECT:
      const isSelectRequired = isFieldRequired(field, getFieldValue);
      const selectDependencyField = field.requiredOn?.fieldName;

      return selectDependencyField ? (
        <Form.Item shouldUpdate noStyle dependencies={[selectDependencyField]}>
          {({getFieldValue: getFieldValueDynamic}) => {
            const isSelectRequiredDynamic = isFieldRequired(
              field,
              getFieldValueDynamic
            );

            return (
              <>
                <Form.Item
                  name={field.fieldName}
                  label={headerLabelContainer(
                    field?.heading as any,
                    displayFieldLabel
                  )}
                  className={field?.colSpan ? '' : 'col-span-center'}
                  labelCol={field.colSpan ? {span: 24} : undefined}
                  tooltip={
                    field.tooltip
                      ? {
                          title: field.tooltip,
                          icon: <InfoCircleOutlined />
                          // overlayStyle: tooltipCSS
                        }
                      : ''
                  }
                  rules={
                    isSelectRequiredDynamic
                      ? [
                          {
                            required: true,
                            message: `${field.fieldLabel} is required`,
                            validator: async (_, value) => {
                              if (
                                !value ||
                                (typeof value === 'string' &&
                                  value.length === 0)
                              ) {
                                return await Promise.reject(
                                  new Error(`${field.fieldLabel} is required`)
                                );
                              }

                              await Promise.resolve();
                            }
                          }
                        ]
                      : []
                  }
                >
                  <Select
                    defaultValue={field.defaultValue}
                    style={{
                      width: field.inputWidth || 'fit-content',
                      maxWidth: '100%',
                      minWidth: '150px'
                    }}
                    options={transformNestedOptions(
                      sortedOptions(field?.options) || []
                    )}
                    optionRender={(option) => (
                      <span
                        style={{whiteSpace: 'normal', wordBreak: 'break-word'}}
                      >
                        {option.data.label}
                      </span>
                    )}
                    placeholder="Please select"
                    disabled={field.disabled}
                    onChange={(value) => {
                      handleOnChange(field.fieldName, value);
                    }}
                    showSearch
                    optionFilterProp="label"
                    popupMatchSelectWidth={false}
                    popupClassName="modal-select-dropdown"
                  />
                </Form.Item>
                {(getFieldValueDynamic(field.fieldName) === 'Other' ||
                  showTextBox[field.fieldName]) && (
                  <Form.Item name={field.fieldName + '_other'}>
                    <Input placeholder="Other" />
                  </Form.Item>
                )}
              </>
            );
          }}
        </Form.Item>
      ) : (
        <>
          <Form.Item
            name={field.fieldName}
            label={headerLabelContainer(
              field?.heading as any,
              displayFieldLabel
            )}
            className={field?.colSpan ? '' : 'col-span-center'}
            labelCol={field.colSpan ? {span: 24} : undefined}
            tooltip={
              field.tooltip
                ? {
                    title: field.tooltip,
                    icon: <InfoCircleOutlined />
                    // overlayStyle: tooltipCSS
                  }
                : ''
            }
            rules={
              isSelectRequired
                ? [
                    {
                      required: true,
                      message: `${field.fieldLabel} is required`,
                      validator: async (_, value) => {
                        if (
                          !value ||
                          (typeof value === 'string' && value.length === 0)
                        ) {
                          return await Promise.reject(
                            new Error(`${field.fieldLabel} is required`)
                          );
                        }

                        await Promise.resolve();
                      }
                    }
                  ]
                : []
            }
          >
            <Select
              defaultValue={field.defaultValue}
              style={{
                width: field.inputWidth || 'fit-content',
                maxWidth: '100%',
                minWidth: '150px'
              }}
              options={transformNestedOptions(
                sortedOptions(field?.options) || []
              )}
              optionRender={(option) => (
                <span style={{whiteSpace: 'normal', wordBreak: 'break-word'}}>
                  {option.data.label}
                </span>
              )}
              placeholder="Please select"
              disabled={field.disabled}
              onChange={(value) => {
                handleOnChange(field.fieldName, value);
              }}
              showSearch
              optionFilterProp="label"
              popupMatchSelectWidth={false}
              popupClassName="modal-select-dropdown"
            />
          </Form.Item>
          {(getFieldValue(field.fieldName) === 'Other' ||
            showTextBox[field.fieldName]) && (
            <Form.Item name={field.fieldName + '_other'}>
              <Input placeholder="Other" />
            </Form.Item>
          )}
        </>
      );

    case WYSIWYG:
      return field?.conditions ? (
        <Form.Item dependencies={field?.conditions.dependsOn} noStyle>
          {({getFieldValue}) => {
            let shouldShow = false;

            if (field?.conditions?.shouldShow) {
              shouldShow = evaluateExpression(
                field?.conditions?.shouldShow.expression,
                getFieldValue([])
              );
              if (!shouldShow) {
                const currentValue = getFieldValue(field.fieldName);
                if (
                  currentValue !== undefined &&
                  currentValue !== null &&
                  currentValue !== ''
                ) {
                  setFieldValue(field.fieldName, undefined);
                }
              }

              return shouldShow && <WysiwygInput field={field} />;
            }

            if (field?.conditions?.shouldDisable) {
              field.disabled = evaluateExpression(
                field?.conditions?.shouldDisable.expression,
                getFieldValue([])
              );
              field.isRequired = false;
            }

            if (field?.conditions?.shouldRequired && field?.disabled !== true) {
              field.isRequired = evaluateExpression(
                field?.conditions?.shouldRequired.expression,
                getFieldValue([])
              );
            }

            return <WysiwygInput field={field} />;
          }}
        </Form.Item>
      ) : (
        <WysiwygInput field={field} />
      );

    case SELECT_WITH_OTHER_TEXTBOX:
      return (
        <>
          <Form.Item
            name={field.fieldName}
            label={displayFieldLabel}
            className={field?.colSpan ? '' : 'col-span-center'}
            labelCol={field.colSpan ? {span: 24} : undefined}
            tooltip={
              field.tooltip
                ? {
                    title: field.tooltip,
                    icon: <InfoCircleOutlined />
                    // overlayStyle: tooltipCSS
                  }
                : ''
            }
          >
            <Select
              defaultValue={field.defaultValue}
              style={{
                width: field.inputWidth || 'fit-content',
                maxWidth: '100%',
                minWidth: '150px'
              }}
              options={transformNestedOptions(
                sortedOptions(field?.options) || []
              )}
              optionRender={(option) => (
                <span style={{whiteSpace: 'normal', wordBreak: 'break-word'}}>
                  {option.data.label}
                </span>
              )}
              showSearch
              placeholder="Please select"
              disabled={
                field.disabled ||
                isFieldDisabled(field, getFieldValue, (field as any).rowId)
              }
              onChange={(value) => {
                handleOnChange(field.fieldName, value);
              }}
              popupMatchSelectWidth={false}
            />
          </Form.Item>
          {(() => {
            const fieldValue = getFieldValue(field.fieldName);
            const hasLength =
              fieldValue &&
              typeof fieldValue === 'string' &&
              fieldValue.length > 0;
            const shouldShow =
              (hasLength &&
                (fieldValue === 'Other' ||
                  fieldValue === 'Other, please specify' ||
                  (fieldValue?.startsWith('Other ') &&
                    fieldValue?.endsWith('please specify')))) ||
              showTextBox[field.fieldName];

            return shouldShow;
          })() && (
            <Form.Item
              name={field.fieldName + '_other'}
              label="Please specify"
              rules={[{required: true, message: 'Please specify'}]}
            >
              <Input placeholder="Please specify" />
            </Form.Item>
          )}
        </>
      );

    case MULTISELECT:
      const isMultiSelectRequired = isFieldRequired(field, getFieldValue);

      return field && (field as any).dependsOn && (field as any).showWhen ? (
        <Form.Item shouldUpdate noStyle>
          {({getFieldValue, setFieldValue}) => {
            let finalValue: any;
            if ((field as any).relatedTableKey) {
              const dependsOnTableKeyValue =
                getFieldValue((field as any).relatedTableKey) ?? [];
              const tableData = Array.isArray(dependsOnTableKeyValue)
                ? dependsOnTableKeyValue
                : [dependsOnTableKeyValue];
              finalValue = tableData[0]?.[(field as any).dependsOn ?? ''];
            } else {
              finalValue = getFieldValue((field as any).dependsOn);
            }

            // Handle both array and string values
            const showWhenArray = Array.isArray(field.showWhen)
              ? field.showWhen
              : [field.showWhen];
            let shouldShow = false;

            if (Array.isArray(finalValue)) {
              // If finalValue is an array (MULTISELECT), check if any value matches
              shouldShow = finalValue.some((val: any) =>
                showWhenArray.includes(val)
              );
            } else if (finalValue !== undefined && finalValue !== null) {
              // If finalValue is a string or single value, check if it's in showWhen
              shouldShow = showWhenArray.includes(finalValue);
            }

            if (!shouldShow) {
              const currentValue = getFieldValue(field.fieldName);
              if (
                currentValue !== undefined &&
                currentValue !== null &&
                currentValue !== ''
              ) {
                setFieldValue(field.fieldName, undefined);
              }
            }

            return shouldShow ? (
              <Form.Item
                name={field.fieldName}
                label={headerLabelContainer(
                  field?.heading as any,
                  displayFieldLabel
                )}
                tooltip={
                  field.tooltip
                    ? {
                        title: field.tooltip,
                        icon: <InfoCircleOutlined />
                        // overlayStyle: tooltipCSS
                      }
                    : ''
                }
              >
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Please select"
                  defaultValue={field.defaultValue}
                  style={{width: '40%'}}
                  showSearch
                  options={transformNestedOptions(
                    sortedOptions(field?.options) || []
                  )}
                  optionRender={(option) => (
                    <span
                      style={{whiteSpace: 'normal', wordBreak: 'break-word'}}
                    >
                      {option.data.label}
                    </span>
                  )}
                  disabled={
                    field.disabled ||
                    isFieldDisabled(field, getFieldValue, (field as any).rowId)
                  }
                />
              </Form.Item>
            ) : (
              <></>
            );
          }}
        </Form.Item>
      ) : (
        <Form.Item
          name={field.fieldName}
          label={headerLabelContainer(field?.heading as any, displayFieldLabel)}
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />
                  // overlayStyle: tooltipCSS
                }
              : ''
          }
          rules={[
            ...(isMultiSelectRequired
              ? [
                  {
                    required: true,
                    message: `${field.fieldLabel} is required`,
                    validator: async (_: any, value: any) => {
                      if (
                        !value ||
                        !Array.isArray(value) ||
                        value.length === 0
                      ) {
                        return await Promise.reject(
                          new Error(`${field.fieldLabel} is required`)
                        );
                      }

                      await Promise.resolve();
                    }
                  }
                ]
              : []),
            ...(field.exclusiveOption
              ? [
                  {
                    validator: async (_: any, value: any) => {
                      if (
                        Array.isArray(value) &&
                        value.length > 1 &&
                        value.includes(field.exclusiveOption?.value)
                      ) {
                        return await Promise.reject(
                          new Error(field.exclusiveOption?.message)
                        );
                      }

                      await Promise.resolve();
                    }
                  }
                ]
              : [])
          ]}
        >
          <Select
            mode="multiple"
            allowClear
            placeholder="Please select"
            defaultValue={field.defaultValue}
            style={{
              width: field.inputWidth || 'fit-content',
              maxWidth: '100%',
              minWidth: '150px'
            }}
            showSearch
            options={transformNestedOptions(
              sortedOptions(field?.options) || []
            )}
            optionRender={(option) => (
              <span style={{whiteSpace: 'normal', wordBreak: 'break-word'}}>
                {option.data.label}
              </span>
            )}
            disabled={
              field.disabled ||
              isFieldDisabled(field, getFieldValue, (field as any).rowId)
            }
            popupMatchSelectWidth={false}
          />
        </Form.Item>
      );

    case MULTISELECT_WITH_OTHER_TEXTBOX: {
      const renderMultiSelectWithOther = (
        getFV: (name: any) => any
      ): React.ReactNode => (
        <>
          <Form.Item
            name={field.fieldName}
            label={headerLabelContainer(
              field?.heading as any,
              displayFieldLabel
            )}
            tooltip={
              field.tooltip
                ? {
                    title: field.tooltip,
                    icon: <InfoCircleOutlined />
                    // overlayStyle: tooltipCSS
                  }
                : ''
            }
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="Please select"
              defaultValue={field.defaultValue}
              style={{width: '40%'}}
              options={transformNestedOptions(
                sortedOptions(field?.options) || []
              )}
              optionRender={(option) => (
                <span style={{whiteSpace: 'normal', wordBreak: 'break-word'}}>
                  {option.data.label}
                </span>
              )}
              disabled={
                field.disabled ||
                isFieldDisabled(field, getFV, (field as any).rowId)
              }
              onChange={(value) => {
                handleMultiSelectOnChange(field.fieldName, value);
              }}
            />
          </Form.Item>
          {(() => {
            const fieldValue = getFV(field.fieldName);
            // For multiselect, fieldValue is an array
            const isArray = Array.isArray(fieldValue);
            const shouldShow =
              (isArray &&
                fieldValue.length > 0 &&
                (fieldValue.includes('Other') ||
                  fieldValue.includes('Other, please specify') ||
                  fieldValue.some(
                    (item: string) =>
                      typeof item === 'string' &&
                      item.startsWith('Other ') &&
                      item.endsWith('please specify')
                  ))) ||
              showTextBox[field.fieldName];

            return shouldShow;
          })() && (
            <Form.Item
              name={field.fieldName + '_other'}
              label="Please specify"
              rules={[{required: true, message: 'Please specify'}]}
            >
              <Input placeholder="Please specify" />
            </Form.Item>
          )}
        </>
      );

      if (!((field as any).dependsOn && (field as any).showWhen)) {
        return renderMultiSelectWithOther(getFieldValue);
      }

      return (
        <Form.Item shouldUpdate noStyle>
          {({getFieldValue: getFVD, setFieldValue}) => {
            const shouldShow = matchesShowWhen(
              field,
              getDependsOnValue(field, getFVD)
            );

            if (!shouldShow) {
              const currentValue = getFVD(field.fieldName);
              if (
                currentValue !== undefined &&
                currentValue !== null &&
                currentValue !== ''
              ) {
                setFieldValue(field.fieldName, undefined);
                setFieldValue(field.fieldName + '_other', undefined);
              }

              return <></>;
            }

            return renderMultiSelectWithOther(getFVD);
          }}
        </Form.Item>
      );
    }
    case CITY:
      return (
        <Form.Item noStyle dependencies={[field.relatedKey]}>
          {({getFieldValue}) => {
            return (
              <Form.Item
                name={field.fieldName}
                label={displayFieldLabel}
                tooltip={
                  field.tooltip
                    ? {
                        title: field.tooltip,
                        icon: <InfoCircleOutlined />
                      }
                    : ''
                }
                className={field?.colSpan ? '' : 'col-span-center'}
                labelCol={field.colSpan ? {span: 24} : undefined}
              >
                <Select
                  showSearch
                  defaultValue={field.defaultValue}
                  style={{width: 150}}
                  options={sortedOptions(
                    cityOptions.filter(
                      (item) =>
                        item.state_id === getFieldValue([field.relatedKey])
                    )
                  )}
                  optionRender={(option) => (
                    <span
                      style={{whiteSpace: 'normal', wordBreak: 'break-word'}}
                    >
                      {option.data.label}
                    </span>
                  )}
                  filterOption={filterSelectOptions}
                  placeholder="Select City"
                  popupMatchSelectWidth={false}
                />
              </Form.Item>
            );
          }}
        </Form.Item>
      );
    case STATE:
      return (
        <Form.Item
          name={field.fieldName}
          label={displayFieldLabel}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />
                }
              : ''
          }
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field.colSpan ? {span: 24} : undefined}
          shouldUpdate
        >
          <Select
            showSearch
            defaultValue={field.defaultValue}
            style={{width: 150}}
            options={sortedOptions(
              stateOptions.filter((item) => item?.country_id === 101)
            )}
            optionRender={(option) => (
              <span style={{whiteSpace: 'normal', wordBreak: 'break-word'}}>
                {option.data.label}
              </span>
            )}
            filterOption={filterSelectOptions}
            placeholder="Select State"
            onChange={() => {
              setFieldValue([field.relatedKey], undefined);
            }}
            popupMatchSelectWidth={false}
          />
        </Form.Item>
      );
    case COUNTRY:
      return (
        <Form.Item
          name={field.fieldName}
          label={displayFieldLabel}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />
                  // overlayStyle: tooltipCSS
                }
              : ''
          }
        >
          <Select
            showSearch
            defaultValue={field.defaultValue}
            style={{width: field.inputWidth || '150px'}}
            options={sortedOptions(countryOptions)}
            optionRender={(option) => (
              <span style={{whiteSpace: 'normal', wordBreak: 'break-word'}}>
                {option.data.label}
              </span>
            )}
            filterOption={filterSelectOptions}
            disabled={field.disabled}
            mode={field.selectionModeMultiple}
            popupMatchSelectWidth={false}
          />
        </Form.Item>
      );
    case NUMBER:
      const isNumberRequired = isFieldRequired(field, getFieldValue);
      const numberFieldName = field.units
        ? [field.fieldName, 'value']
        : field.fieldName;
      const numberFieldValue = getFieldValue(numberFieldName);

      // Check if error message should be shown based on condition
      const shouldShowErrorMessage =
        field?.errorMessage &&
        field?.errorMessageCondition &&
        numberFieldValue !== null &&
        numberFieldValue !== '';
      let showError = false;
      let errorHelpMessage = '';

      if (shouldShowErrorMessage && field.errorMessageCondition) {
        const numValue = Number(numberFieldValue);
        const {condition, numericValue} = field.errorMessageCondition;

        if (!isNaN(numValue)) {
          if (condition === conditionType.GREATER_THAN) {
            showError = numValue > numericValue;
          } else if (condition === conditionType.LESS_THAN) {
            showError = numValue < numericValue;
          }

          if (showError) {
            errorHelpMessage = field.errorMessage || '';
          }
        }
      }

      return (
        <Form.Item
          name={field.units ? [field.fieldName, 'value'] : field.fieldName}
          label={headerLabelContainer(field?.heading as any, displayFieldLabel)}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />
                }
              : ''
          }
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
          help={showError ? errorHelpMessage : undefined}
          validateStatus={showError ? 'error' : undefined}
          rules={
            isNumberRequired
              ? [
                  {
                    required: true,
                    message: `${field.fieldLabel} is required`,
                    validator: async (_, value) => {
                      if (
                        value === undefined ||
                        value === null ||
                        value === '' ||
                        (typeof value === 'number' && isNaN(value))
                      ) {
                        return await Promise.reject(
                          new Error(`${field.fieldLabel} is required`)
                        );
                      }

                      await Promise.resolve();
                    }
                  }
                ]
              : []
          }
        >
          <NumberInput
            min={field.min}
            max={field.max}
            defaultValue={field.defaultValue}
            placeholder={field.placeHolder}
            disabled={field.disabled}
            isFormatted={field?.isFormatted}
            inputWidth={field.units ? '300px' : '250px'}
            roundTo={field?.roundTo}
            addonAfter={
              !field.units ? null : typeof field.units === 'string' ? (
                <Form.Item
                  name={field.units && [field.fieldName, 'unit']}
                  initialValue={field.units}
                  noStyle
                >
                  {field.units}
                </Form.Item>
              ) : (
                <Form.Item
                  name={field.units && [field.fieldName, 'unit']}
                  noStyle
                >
                  <Select
                    style={{width: 140}}
                    placeholder="Select Unit"
                    options={sortedOptions(field?.units)}
                    optionRender={(option) => (
                      <span
                        style={{whiteSpace: 'normal', wordBreak: 'break-word'}}
                      >
                        {option.data.label}
                      </span>
                    )}
                    showSearch
                    popupMatchSelectWidth={false}
                  />
                </Form.Item>
              )
            }
          />
        </Form.Item>
      );
    case PERCENTAGE: {
      const isPercentageRequired = isFieldRequired(field, getFieldValue);
      const percentageDependencyField = field.requiredOn?.fieldName;
      const percentageRules = (required: boolean) => [
        ...(required
          ? [
              {
                required: true,
                message: `${field.fieldLabel} is required`,
                validator: async (_: unknown, value: unknown) => {
                  if (
                    value === undefined ||
                    value === null ||
                    value === '' ||
                    (typeof value === 'number' && isNaN(value))
                  ) {
                    return await Promise.reject(
                      new Error(`${field.fieldLabel} is required`)
                    );
                  }
                  await Promise.resolve();
                }
              }
            ]
          : []),
        {pattern: RegEx.percentage, message: CommonErrors.percentageError}
      ];

      return percentageDependencyField ? (
        <Form.Item
          shouldUpdate
          noStyle
          dependencies={[percentageDependencyField]}
        >
          {({getFieldValue: getFieldValueDynamic}) => {
            const isPercentageRequiredDynamic = isFieldRequired(
              field,
              getFieldValueDynamic
            );

            return (
              <Form.Item
                name={field.fieldName}
                label={headerLabelContainer(
                  field?.heading as any,
                  displayFieldLabel
                )}
                required={isPercentageRequiredDynamic}
                tooltip={
                  field.tooltip
                    ? {
                        title: field.tooltip,
                        icon: <InfoCircleOutlined />
                        // overlayStyle: tooltipCSS
                      }
                    : ''
                }
                rules={percentageRules(isPercentageRequiredDynamic)}
                className={field?.colSpan ? '' : 'col-span-center'}
                labelCol={field?.colSpan ? {span: 24} : undefined}
              >
                <NumberInput
                  min={1}
                  max={100}
                  defaultValue={field.defaultValue}
                  name={field.fieldName}
                  disabled={field.disabled}
                  inputWidth={'160px'}
                  addonAfter={<Form.Item noStyle>{field.units}</Form.Item>}
                />
              </Form.Item>
            );
          }}
        </Form.Item>
      ) : (
        <Form.Item
          name={field.fieldName}
          label={displayFieldLabel}
          required={isPercentageRequired}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />
                  // overlayStyle: tooltipCSS
                }
              : ''
          }
          rules={percentageRules(isPercentageRequired)}
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
        >
          <NumberInput
            min={1}
            max={100}
            defaultValue={field.defaultValue}
            name={field.fieldName}
            disabled={field.disabled}
            inputWidth={'160px'}
            addonAfter={<Form.Item noStyle>{field.units}</Form.Item>}
          />
        </Form.Item>
      );
    }
    case RADIOGROUP: {
      const isRadioGroupRequired = isFieldRequired(field, getFieldValue);
      const radioDependencyField = field.requiredOn?.fieldName;
      const radioRequiredRules = (required: boolean) =>
        required
          ? [
              {
                required: true,
                message: `${field.fieldLabel} is required`,
                validator: async (_: unknown, value: unknown) => {
                  if (
                    value === undefined ||
                    value === null ||
                    (typeof value === 'string' && value.length === 0)
                  ) {
                    return await Promise.reject(
                      new Error(`${field.fieldLabel} is required`)
                    );
                  }
                  await Promise.resolve();
                }
              }
            ]
          : [];

      return radioDependencyField ? (
        <Form.Item shouldUpdate noStyle dependencies={[radioDependencyField]}>
          {({getFieldValue: getFieldValueDynamic}) => {
            const isRadioGroupRequiredDynamic = isFieldRequired(
              field,
              getFieldValueDynamic
            );

            return (
              <>
                <Form.Item
                  name={field.fieldName}
                  label={headerLabelContainer(
                    field?.heading as any,
                    displayFieldLabel
                  )}
                  tooltip={
                    field.tooltip
                      ? {
                          title: field.tooltip,
                          icon: <InfoCircleOutlined />
                        }
                      : ''
                  }
                  className={field?.colSpan ? '' : 'col-span-center'}
                  labelCol={field?.colSpan ? {span: 24} : undefined}
                  rules={radioRequiredRules(isRadioGroupRequiredDynamic)}
                >
                  <Radio.Group
                    name={field.fieldName}
                    options={field.options}
                    value={field.defaultValue}
                    onChange={(e) => {
                      handleOnChange(field.fieldName, e.target.value);
                    }}
                    disabled={field.disabled}
                  />
                </Form.Item>
                <Space size="small" />
                {(getFieldValueDynamic(field.fieldName) === 'Other' ||
                  showTextBox[field.fieldName]) && (
                  <Form.Item name={`${field.fieldName}_other`}>
                    <Input
                      placeholder="Other"
                      type={
                        getFieldValueDynamic(field.fieldName) === 'Other'
                          ? 'text'
                          : 'hidden'
                      }
                    />
                  </Form.Item>
                )}
              </>
            );
          }}
        </Form.Item>
      ) : (
        <>
          <Form.Item
            name={field.fieldName}
            label={headerLabelContainer(
              field?.heading as any,
              displayFieldLabel
            )}
            tooltip={
              field.tooltip
                ? {
                    title: field.tooltip,
                    icon: <InfoCircleOutlined />
                  }
                : ''
            }
            className={field?.colSpan ? '' : 'col-span-center'}
            labelCol={field?.colSpan ? {span: 24} : undefined}
            rules={radioRequiredRules(isRadioGroupRequired)}
          >
            <Radio.Group
              name={field.fieldName}
              options={field.options}
              value={field.defaultValue}
              onChange={(e) => {
                handleOnChange(field.fieldName, e.target.value);
              }}
              disabled={field.disabled}
            />
          </Form.Item>
          <Space size="small"></Space>
          {(getFieldValue(field.fieldName) === 'Other' ||
            showTextBox[field.fieldName]) && (
            <Form.Item name={`${field.fieldName}_other`}>
              <Input
                placeholder="Other"
                type={
                  getFieldValue(field.fieldName) === 'Other' ? 'text' : 'hidden'
                }
              />
            </Form.Item>
          )}
        </>
      );
    }

    case GHG_EMISSIONS_REPORTING_10:
      return (
        <>
          <Form.Item
            name={field.fieldName}
            label={displayFieldLabel}
            className={field?.colSpan ? '' : 'col-span-center'}
            labelCol={field?.colSpan ? {span: 24} : undefined}
            tooltip={
              field.tooltip
                ? {
                    title: field.tooltip,
                    icon: <InfoCircleOutlined />
                  }
                : ''
            }
          >
            <Radio.Group
              name={field.fieldName}
              options={field.options}
              value={field.defaultValue}
              onChange={handleOnChangeGHGEmission}
              disabled={field.disabled}
            ></Radio.Group>
          </Form.Item>
          {(ghgEmissionReportingQuestion10 ||
            getFieldValue(field.fieldName) === 'Yes') && (
            <>
              {field?.fieldsListOnTriggerYes?.map(
                (formField: IField, index) => (
                  <div key={index} style={{marginBottom: 8}}>
                    <div
                      style={{
                        display: 'inline-block',
                        width: '100%'
                      }}
                    >
                      {formField.fieldType === inputType.HEADING && (
                        <h4>
                          {formField.fieldLabel}
                          {field.tooltip ? (
                            <Tooltip
                              title={formField.tooltip}
                              autoAdjustOverflow={true}
                              arrow={false}
                              overlayClassName="esg-tooltip"
                            >
                              <ExclamationCircleOutlined className="iButtonIcon" />
                            </Tooltip>
                          ) : (
                            ''
                          )}
                        </h4>
                      )}
                      {formField.fieldType === inputType.CHECKBOX && (
                        <Form.Item
                          name={formField.fieldName}
                          label={formField.fieldLabel}
                          className={
                            formField?.colSpan ? '' : 'col-span-center'
                          }
                          labelCol={formField?.colSpan ? {span: 24} : undefined}
                          tooltip={
                            formField.tooltip
                              ? {
                                  title: formField.tooltip,
                                  icon: <InfoCircleOutlined />
                                }
                              : ''
                          }
                          valuePropName="checked"
                        >
                          <Checkbox
                            disabled={formField.disabled}
                            name={formField.fieldName}
                          ></Checkbox>
                        </Form.Item>
                      )}
                      {formField.fieldType === inputType.TEXTAREA && (
                        <Form.Item
                          name={formField.fieldName}
                          label={formField.fieldLabel}
                          tooltip={
                            formField.tooltip
                              ? {
                                  title: formField.tooltip,
                                  icon: <InfoCircleOutlined />
                                }
                              : ''
                          }
                          className={field?.colSpan ? '' : 'col-span-center'}
                          labelCol={formField?.colSpan ? {span: 24} : undefined}
                          style={{marginBottom: '0px'}}
                        >
                          <TextArea
                            autoSize={{minRows: 4, maxRows: 20}}
                            disabled={formField.disabled}
                            placeholder={formField.placeHolder}
                            style={{
                              borderBottomLeftRadius: '0'
                            }}
                          />
                        </Form.Item>
                      )}
                    </div>
                  </div>
                )
              )}
            </>
          )}
          {!ghgEmissionReportingQuestion10 &&
            getFieldValue(field.fieldName) === 'No' && (
              <>
                <Form.Item
                  name={field?.fieldsOnTriggerNo?.fieldName}
                  label={field?.fieldsOnTriggerNo?.fieldLabel}
                  tooltip={
                    field?.fieldsOnTriggerNo?.tooltip
                      ? {
                          title: field?.fieldsOnTriggerNo?.tooltip,
                          icon: <InfoCircleOutlined />
                        }
                      : ''
                  }
                  className={field?.colSpan ? '' : 'col-span-center'}
                  labelCol={field?.colSpan ? {span: 24} : undefined}
                  style={{}}
                >
                  <TextArea
                    autoSize={{minRows: 4, maxRows: 20}}
                    disabled={field?.fieldsOnTriggerNo?.disabled}
                    placeholder={field?.fieldsOnTriggerNo?.placeHolder}
                    style={{borderBottomRightRadius: '0'}}
                  />
                </Form.Item>
              </>
            )}
        </>
      );

    case RADIOGROUP_IF_YES_ADDMORE_TEXTAREA:
      return (
        <>
          <Form.Item
            name={field.fieldName}
            label={displayFieldLabel}
            tooltip={
              field.tooltip
                ? {
                    title: field.tooltip,
                    icon: <InfoCircleOutlined />
                    // overlayStyle: tooltipCSS
                  }
                : ''
            }
          >
            <Radio.Group
              name={field.fieldName}
              options={field.options}
              value={field.defaultValue}
              onChange={(e) => {
                handleOnChange14(field.fieldName, e.target.value);
              }}
              disabled={field.disabled}
            ></Radio.Group>
          </Form.Item>
          {(getFieldValue(field.fieldName) === 'Yes' ||
            radioGroupIfYesThenShowTextArea[field.fieldName]) && (
            <Form.List
              name={field.fieldName + '_yes'}
              initialValue={[blankObj]}
            >
              {(fields, {add, remove}) => (
                <>
                  {fields.map(({key, name}) => (
                    <div key={key} style={{marginBottom: 8}}>
                      <div
                        style={{
                          display: 'inline-block',
                          width: 'calc(100% - 24px)'
                        }}
                      >
                        <Form.Item name={[name, field.fieldName]}>
                          <TextArea
                            style={{verticalAlign: 'middle'}}
                            disabled={field.disabled}
                            autoSize={{minRows: 4, maxRows: 10}}
                          />
                        </Form.Item>
                      </div>
                      {fields.length > 1 ? (
                        <div
                          style={{
                            display: 'inline-block',
                            marginLeft: 8
                          }}
                        >
                          <MinusCircleOutlined
                            onClick={() => {
                              remove(name);
                            }}
                          />
                        </div>
                      ) : null}
                    </div>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => {
                        add();
                      }}
                      icon={<PlusOutlined />}
                      style={{width: 200}}
                      // onKeyDown={(e) => {
                      //   e.key === 'Enter' && add();
                      // }}
                    >
                      Add
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          )}
        </>
      );

    case ADDMORE_TEXTAREA:
      // eslint-disable-next-line no-lone-blocks
      {
        field?.formList?.formFields.map(
          // eslint-disable-next-line array-callback-return
          (fieldItem) => {
            if (
              fieldItem.hasOwnProperty('fieldName') &&
              (fieldItem.type !== undefined ||
                fieldItem.type !== inputType.HEADING ||
                fieldItem.type !== inputType.HEADING_2 ||
                fieldItem.type !== inputType.HEADING_3)
            ) {
              blankObj[`${fieldItem.fieldName}`] = '';
            }
          }
        );
      }

      return (
        <Form.Item shouldUpdate>
          {() => {
            return (
              <Form.List
                name={field?.formList?.name ?? ''}
                initialValue={[blankObj]}
              >
                {(fields, {add, remove}) => (
                  <>
                    {fields.map(({key, name}) => (
                      <div key={key} style={{display: 'flex', marginBottom: 4}}>
                        {field?.formList?.formFields.map((formField, index) => {
                          return (
                            <div key={index} style={{width: '100%'}}>
                              <Form.Item
                                name={[name, formField.fieldName]}
                                label={formField.fieldLabel}
                                labelCol={{span: 24}}
                                style={
                                  formField.askAIkey
                                    ? {marginBottom: '0px'}
                                    : {}
                                }
                              >
                                {formField.askAIkey ? (
                                  <ReactQuill
                                    placeholder={formField.placeHolder}
                                    readOnly={formField.disabled}
                                    style={{width: '100%'}}
                                  />
                                ) : (
                                  <TextArea
                                    style={{verticalAlign: 'middle'}}
                                    disabled={formField.disabled}
                                    autoSize={{minRows: 4, maxRows: 10}}
                                  />
                                )}
                              </Form.Item>
                              {formField.askAIkey && (
                                <Form.Item style={{marginBottom: '24px'}}>
                                  <PromptSteps
                                    field={formField}
                                    innerFieldName={[
                                      field?.formList?.name ?? '',
                                      name,
                                      formField.fieldName
                                    ]}
                                    getFieldValue={getFieldValue}
                                    setFieldValue={setFieldValue}
                                    setSubmitDisabled={setSubmitDisabled}
                                    userId={userId}
                                    companyId={companyId}
                                  />
                                </Form.Item>
                              )}
                            </div>
                          );
                        })}
                        <div
                          style={{alignContent: 'center', paddingLeft: '1rem'}}
                        >
                          {fields.length > 1 ? (
                            <MinusCircleOutlined
                              onClick={() => {
                                remove(name);
                              }}
                            />
                          ) : null}
                        </div>
                      </div>
                    ))}
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() => {
                          add();
                        }}
                        icon={<PlusOutlined />}
                        style={{width: 200}}
                        // onKeyDown={(e) => {
                        //   e.key === 'Enter' && add();
                        // }}
                      >
                        Add
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            );
          }}
        </Form.Item>
      );
    case RADIOGROUP_IF_YES_THEN_TEXTAREA:
      return (
        <>
          <Form.Item
            name={field.fieldName}
            label={headerLabelContainer(
              field?.heading as any,
              displayFieldLabel
            )}
            className={field?.colSpan ? '' : 'col-span-center'}
            labelCol={field?.colSpan ? {span: 24} : undefined}
            tooltip={
              field.tooltip
                ? {
                    title: field.tooltip,
                    icon: <InfoCircleOutlined />
                    // overlayStyle: tooltipCSS
                  }
                : ''
            }
          >
            <Radio.Group
              name={field.fieldName}
              options={field.options}
              value={field.defaultValue}
              onChange={(e) => {
                handleOnChange14(field.fieldName, e.target.value);
              }}
              disabled={field.disabled}
            ></Radio.Group>
          </Form.Item>
          <Space size="small"></Space>
          {(getFieldValue(field.fieldName) === 'Yes' ||
            radioGroupIfYesThenShowTextArea[field.fieldName]) && (
            <>
              <Form.Item
                name={field.fieldName + '_yes'}
                style={field.askAIkey ? {marginBottom: '0px'} : {}}
              >
                {field.askAIkey ? (
                  <ReactQuill
                    placeholder={field.placeHolder}
                    readOnly={field.disabled}
                    style={{
                      width: '100%',
                      display:
                        getFieldValue(field.fieldName) === 'Yes'
                          ? 'inlineBlock'
                          : 'none'
                    }}
                  />
                ) : (
                  <TextArea
                    autoSize={{minRows: 4, maxRows: 10}}
                    disabled={field.disabled}
                    style={{
                      display:
                        getFieldValue(field.fieldName) === 'Yes'
                          ? 'inlineBlock'
                          : 'none'
                    }}
                  />
                )}
              </Form.Item>
              {field.askAIkey && (
                <Form.Item style={{marginBottom: '24px'}}>
                  <PromptSteps
                    field={field}
                    innerFieldName={field.fieldName + '_yes'}
                    getFieldValue={getFieldValue}
                    setFieldValue={setFieldValue}
                    setSubmitDisabled={setSubmitDisabled}
                    userId={userId}
                    companyId={companyId}
                  />
                </Form.Item>
              )}
            </>
          )}
        </>
      );
    case RADIOGROUP_IF_YES_THEN_TEXTBOX:
      return (
        <>
          <Form.Item
            name={field.fieldName}
            label={displayFieldLabel}
            tooltip={
              field.tooltip
                ? {
                    title: field.tooltip,
                    icon: <InfoCircleOutlined />
                    // overlayStyle: tooltipCSS
                  }
                : ''
            }
            className={field?.colSpan ? '' : 'col-span-center'}
            labelCol={field?.colSpan ? {span: 24} : undefined}
          >
            <Radio.Group
              name={field.fieldName}
              options={field.options}
              value={field.defaultValue}
              onChange={(e) => {
                handleOnChange10(field.fieldName, e.target.value);
              }}
              disabled={field.disabled}
            ></Radio.Group>
          </Form.Item>
          <Space size="small"></Space>
          {(getFieldValue(field.fieldName) === 'Yes' ||
            radioGroupIfYesThenShowTextBox[field.fieldName]) && (
            <Form.Item name={field.fieldName + '_yes'}>
              <Input
                disabled={field.disabled}
                type={
                  getFieldValue(field.fieldName) === 'Yes' ? 'text' : 'hidden'
                }
                placeholder={field.placeHolder}
              />
            </Form.Item>
          )}
        </>
      );
    case RADIOGROUP_IF_NO_THEN_TEXTBOX:
      return (
        <>
          <Form.Item
            name={field.fieldName}
            label={displayFieldLabel}
            className={field?.colSpan ? '' : 'col-span-center'}
            labelCol={field?.colSpan ? {span: 24} : undefined}
            tooltip={
              field.tooltip
                ? {
                    title: field.tooltip,
                    icon: <InfoCircleOutlined />
                    // overlayStyle: tooltipCSS
                  }
                : ''
            }
          >
            <Radio.Group
              name={field.fieldName}
              options={field.options}
              value={field.defaultValue}
              onChange={(e) => {
                handleOnChange11(field.fieldName, e.target.value);
              }}
              disabled={field.disabled}
            ></Radio.Group>
          </Form.Item>
          <Space size="small"></Space>
          {(getFieldValue(field.fieldName) === 'No' ||
            radioGroupIfNoThenShowTextBox[field.fieldName]) && (
            <Form.Item name={field.fieldName + '_no'}>
              <TextArea
                style={{verticalAlign: 'top'}}
                autoSize={{minRows: 4, maxRows: 10}}
                disabled={field.disabled}
              />
            </Form.Item>
          )}
        </>
      );
    case RADIOGROUP_YES_THEN_SHOW_TABLE:
      return (
        <>
          <Form.Item
            name={field.fieldName}
            label={displayFieldLabel}
            className={field?.colSpan ? '' : 'col-span-center'}
            labelCol={field?.colSpan ? {span: 24} : undefined}
            tooltip={
              field.tooltip
                ? {
                    title: field.tooltip,
                    icon: <InfoCircleOutlined />
                    // overlayStyle: tooltipCSS
                  }
                : ''
            }
          >
            <Radio.Group
              name={field.fieldName}
              options={field.options}
              value={field.defaultValue}
              onChange={(e) => {
                handleOnChange11(field.fieldName, e.target.value);
              }}
              disabled={field.disabled}
            ></Radio.Group>
          </Form.Item>
          <Space size="small"></Space>
          {(getFieldValue(field.fieldName) === 'No' ||
            radioGroupIfNoThenShowTextBox[field.fieldName]) && (
            <Form.Item name={field.fieldName + '_no'}>
              <TextArea
                style={{verticalAlign: 'top'}}
                autoSize={{minRows: 4, maxRows: 10}}
                disabled={field.disabled}
              />
            </Form.Item>
          )}
        </>
      );
    case RADIOGROUP_NO_THEN_SHOW_TABLE:
      return (
        <>
          <Form.Item
            name={field.fieldName}
            label={displayFieldLabel}
            className={field?.colSpan ? '' : 'col-span-center'}
            labelCol={field?.colSpan ? {span: 24} : undefined}
            tooltip={
              field.tooltip
                ? {
                    title: field.tooltip,
                    icon: <InfoCircleOutlined />
                    // overlayStyle: tooltipCSS
                  }
                : ''
            }
          >
            <Radio.Group
              name={field.fieldName}
              options={field.options}
              value={field.defaultValue}
              disabled={field.disabled}
            ></Radio.Group>
          </Form.Item>
          <Space size="small"></Space>
        </>
      );
    case CHECKBOX:
      return (
        <Form.Item
          name={field.fieldName}
          label={displayFieldLabel}
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />
                  // overlayStyle: tooltipCSS
                }
              : ''
          }
          valuePropName="checked"
        >
          <Checkbox disabled={field.disabled} name={field.fieldName}></Checkbox>
        </Form.Item>
      );
    case CHECKBOXGROUP: {
      const validationData = field?.autoPopulate
        ? field?.validationData
        : undefined;

      let fixedColumn: any[] | undefined;

      if (validationData && field.fieldName) {
        const fullKey = field.fieldName;

        fixedColumn = validationData?.[fullKey]?.fixedColumn;

        if (
          (!fixedColumn || fixedColumn.length === 0) &&
          typeof fullKey === 'string'
        ) {
          const lastUnderscoreIndex = fullKey.lastIndexOf('_');
          if (lastUnderscoreIndex > -1) {
            const baseKey = fullKey.slice(0, lastUnderscoreIndex);
            fixedColumn = validationData?.[baseKey]?.fixedColumn;
          }
        }
      }

      const dynamicOptions =
        Array.isArray(fixedColumn) && fixedColumn.length > 0
          ? fixedColumn.map((col: any, index: number) => {
              const value =
                col?.rowId ??
                col?.id ??
                col?.value ??
                col?.scopeLabel ??
                col?.label ??
                `${index + 1}`;
              const label =
                col?.scopeLabel ??
                col?.label ??
                col?.name ??
                col?.title ??
                `Item ${index + 1}`;

              return {
                value,
                label
              };
            })
          : undefined;

      const checkboxOptions = dynamicOptions ?? field.options;

      return (
        <Form.Item
          name={field.fieldName}
          label={headerLabelContainer(field?.heading as any, displayFieldLabel)}
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />
                  // overlayStyle: tooltipCSS
                }
              : ''
          }
        >
          <Checkbox.Group
            options={checkboxOptions}
            defaultValue={
              field.defaultValue !== undefined
                ? [field.defaultValue]
                : undefined
            }
            disabled={field.disabled}
          ></Checkbox.Group>
        </Form.Item>
      );
    }
    case CHECKBOX_GLAO_QUESTION_40_TO_48:
      return (
        <>
          <Form.Item
            name={field.fieldName}
            label={displayFieldLabel}
            tooltip={
              field.tooltip
                ? {
                    title: field.tooltip,
                    icon: <InfoCircleOutlined />
                    // overlayStyle: tooltipCSS
                  }
                : ''
            }
            valuePropName="checked"
          >
            <Checkbox
              name={field.fieldName}
              onChange={(e) => {
                handleOnChange2(field.fieldName, e.target.checked);
              }}
              disabled={field.disabled}
            ></Checkbox>
          </Form.Item>
          <Space size="small"></Space>
          {(getFieldValue(field.fieldName) ||
            glaoQuestion40To48showTextBox[field.fieldName]) && (
            <Form.Item name={field.fieldName + '_yes'}>
              <Input
                disabled={field.disabled}
                type={getFieldValue(field.fieldName) ? 'text' : 'hidden'}
              />
            </Form.Item>
          )}
        </>
      );
    case CHECKBOX_NA_THEN_TEXTAREA:
      return (
        <>
          <Form.Item
            name={field.fieldName}
            label={displayFieldLabel}
            tooltip={
              field.tooltip
                ? {
                    title: field.tooltip,
                    icon: <InfoCircleOutlined />
                    // overlayStyle: tooltipCSS
                  }
                : ''
            }
            valuePropName="checked"
          >
            <Checkbox
              name={field.fieldName}
              onChange={handleOnChange13}
              disabled={field.disabled}
            ></Checkbox>
          </Form.Item>
          <Space size="small"></Space>
          {(checkboxNAThenTextarea || getFieldValue(field.fieldName)) && (
            <Form.Item name={field.fieldName + '_NA'}>
              <TextArea
                autoSize
                disabled={field.disabled}
                style={{
                  display: getFieldValue(field.fieldName)
                    ? 'inlineBlock'
                    : 'none'
                }}
              />
            </Form.Item>
          )}
        </>
      );
    case DATEPICKER:
      const isDatePickerRequired = isFieldRequired(field, getFieldValue);

      return (
        <Form.Item
          name={field.fieldName}
          label={displayFieldLabel}
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />
                  // overlayStyle: tooltipCSS
                }
              : ''
          }
          rules={
            isDatePickerRequired
              ? [
                  {
                    required: true,
                    message: `${field.fieldLabel} is required`,
                    validator: async (_, value) => {
                      if (!value) {
                        return await Promise.reject(
                          new Error(`${field.fieldLabel} is required`)
                        );
                      }
                      await Promise.resolve();
                    }
                  }
                ]
              : []
          }
        >
          <MomentDatePicker
            disabled={field.disabled}
            name={field.fieldName}
            format={field.dateFormat ?? 'DD-MM-YYYY'}
            picker={field.pickerType}
            showNow={false}
            disabledDate={(current: any) => {
              return field.restrictFutureDateSelection
                ? moment() <= current
                : false;
            }}
          />
        </Form.Item>
      );
    case DATEPICKER_FINANCIAL_YEAR:
      const isFinancialYearDatePickerRequired = isFieldRequired(
        field,
        getFieldValue
      );
      const isStartDate = !field.relatedDateFieldName;
      const relatedFieldName = field.relatedDateFieldName;

      return (
        <Form.Item
          name={field.fieldName}
          label={displayFieldLabel}
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />
                }
              : ''
          }
          rules={[
            ...(isFinancialYearDatePickerRequired
              ? [
                  {
                    required: true,
                    message: `${field.fieldLabel} is required`,
                    validator: async (_: any, value: any) => {
                      if (!value) {
                        return await Promise.reject(
                          new Error(`${field.fieldLabel} is required`)
                        );
                      }
                      await Promise.resolve();
                    }
                  }
                ]
              : []),
            {
              validator: async (_, value) => {
                if (!value) {
                  await Promise.resolve();

                  return;
                }

                const date = moment.isMoment(value) ? value : moment(value);
                if (!date.isValid()) {
                  return await Promise.reject(
                    new Error('Please enter a valid date')
                  );
                }

                // Validate date is within financial year
                const {preUnits} = useCompanyStore.getState();
                const fiscalYearPreference = preUnits?.financialYear;

                if (
                  !isDateWithinFinancialYear(
                    date,
                    fiscalYearPreference,
                    selectedYear ?? undefined
                  )
                ) {
                  const {startDate, endDate} = getFinancialYearDates(
                    fiscalYearPreference,
                    selectedYear ?? undefined
                  );

                  return await Promise.reject(
                    new Error(
                      `Date must be between ${startDate.format(
                        'DD-MM-YYYY'
                      )} and ${endDate.format('DD-MM-YYYY')}`
                    )
                  );
                }

                // For end date, validate it's after start date
                if (!isStartDate && relatedFieldName) {
                  const startDateValue = getFieldValue(relatedFieldName);
                  if (startDateValue) {
                    const startDate = moment.isMoment(startDateValue)
                      ? startDateValue
                      : moment(startDateValue);
                    if (date.isSameOrBefore(startDate, 'day')) {
                      return await Promise.reject(
                        new Error('End date must be greater than start date')
                      );
                    }
                  }
                }

                await Promise.resolve();
              }
            }
          ]}
        >
          <MomentDatePicker
            disabled={field.disabled}
            name={field.fieldName}
            format={field.dateFormat ?? 'DD-MM-YYYY'}
            picker={field.pickerType}
            showNow={false}
            disabledDate={getFinancialYearDisabledDate(
              useCompanyStore.getState().preUnits?.financialYear,
              selectedYear ?? undefined,
              relatedFieldName,
              isStartDate,
              getFieldValue
            )}
          />
        </Form.Item>
      );
    case DATE_RANGE_PICKER:
      const yearSplit = selectedYear?.split('-');
      const year1 = Number(yearSplit?.[0]);
      const year2 = Number(yearSplit?.[1]);

      const fromDate = moment(`${year1}-04-01T14:00:25.191Z`);
      const toDate = moment(`${year2}-03-31T14:00:25.191Z`);

      return (
        <Form.Item
          name={field.fieldName}
          label={displayFieldLabel}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />
                  // overlayStyle: tooltipCSS
                }
              : ''
          }
          initialValue={field.useSelectedYear ? [fromDate, toDate] : null}
        >
          <RangePicker
            disabled={field.disabled}
            name={field.fieldName}
            format={field.dateFormat ?? 'DD-MM-YYYY'}
            disabledDate={(current: any) => {
              return field.restrictFutureDateSelection
                ? moment() <= current
                : false;
            }}
          />
        </Form.Item>
      );
    case TIMEPICKER:
      return (
        <Form.Item
          name={field.fieldName}
          label={displayFieldLabel}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />
                  // overlayStyle: tooltipCSS
                }
              : ''
          }
        >
          <TimePicker
            disabled={field.disabled}
            name={field.fieldName}
            format={'HH:mm'}
          />
        </Form.Item>
      );
    case FILEUPLOAD:
      const normFile = (e: any) => {
        if (Array.isArray(e)) return e;

        return (
          e?.fileList?.map((file: any) => {
            if (file.status === 'done' && file.response) {
              return {
                ...file,
                filename: file.response.filename,
                originalName: file.response.originalFilename || file.name,
                url: file.response.url,
                response: null,
                filePath: file.response.filePath,
                id: file.response.id
              };
            }

            return file;
          }) || []
        );
      };
      const mapUploadFileListForDisplay = (value: any) => {
        if (!Array.isArray(value)) return [];

        return value.map((item: any, index: number) => {
          return {
            ...item,
            uid: item?.uid ?? item?.id ?? `${index}`,
            name:
              item?.name ??
              item?.originalName ??
              item?.originalFilename ??
              item?.filename ??
              `File ${index + 1}`,
            status: item?.status ?? 'done',
            url: item?.url ?? item?.filePath
          };
        });
      };
      const customUploadRequest: UploadProps['customRequest'] = (option) => {
        const {file, onSuccess, onError, onProgress} = option;

        if (!companyId || !selectedYear) {
          if (onError) {
            onError(new Error('Invalid upload configuration'));
          }

          return;
        }

        if (!(file instanceof Blob)) {
          if (onError) {
            onError(new Error('Invalid file type for upload'));
          }

          return;
        }

        void uploadDocumentAPI({
          file,
          companyId,
          selectedYear,
          framework,
          onProgress
        })
          .then((response: any) => {
            if (onSuccess) {
              onSuccess(response);
            }
          })
          .catch((error: unknown) => {
            if (onError) {
              onError(
                error instanceof Error ? error : new Error(String(error))
              );
            }
          });
      };
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      const uploadProps: UploadProps = {
        name: 'file',
        customRequest: customUploadRequest,
        accept: '.pdf,.jpg,.jpeg,.png,.doc,.docx,.csv,.xlsx',
        beforeUpload: (file) => {
          const isLt10M = file.size / 1024 / 1024 < 10;
          if (!isLt10M) {
            showMessage(
              {status: 'info', message: 'File must be smaller than 10MB!'},
              4
            );

            return Upload.LIST_IGNORE;
          }

          return true;
        }
      };

      const isFileUploadRequired = isFieldRequired(field, getFieldValue);
      const fileUploadDependencyField = field.requiredOn?.fieldName;
      const fileUploadRules = (required: boolean) =>
        required
          ? [
              {
                required: true,
                message: `${field.fieldLabel} is required`,
                validator: async (_: unknown, value: unknown) => {
                  if (!value || (Array.isArray(value) && value.length === 0)) {
                    return await Promise.reject(
                      new Error(`${field.fieldLabel} is required`)
                    );
                  }
                  await Promise.resolve();
                }
              }
            ]
          : [];

      return fileUploadDependencyField ? (
        <Form.Item
          shouldUpdate
          noStyle
          dependencies={[fileUploadDependencyField]}
        >
          {({getFieldValue: getFieldValueDynamic}) => {
            const isFileUploadRequiredDynamic = isFieldRequired(
              field,
              getFieldValueDynamic
            );

            return (
              <Form.Item
                name={field.fieldName}
                label={displayFieldLabel}
                required={isFileUploadRequiredDynamic}
                tooltip={
                  field.tooltip
                    ? {
                        title: field.tooltip,
                        icon: <InfoCircleOutlined />
                        // overlayStyle: tooltipCSS
                      }
                    : ''
                }
                rules={fileUploadRules(isFileUploadRequiredDynamic)}
                getValueFromEvent={normFile}
                getValueProps={(value) => ({
                  fileList: mapUploadFileListForDisplay(value)
                })}
              >
                <Upload {...uploadProps}>
                  <Button icon={<UploadOutlined />}>Click to Upload</Button>
                </Upload>
              </Form.Item>
            );
          }}
        </Form.Item>
      ) : (
        <Form.Item
          name={field.fieldName}
          label={displayFieldLabel}
          required={isFileUploadRequired}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />
                  // overlayStyle: tooltipCSS
                }
              : ''
          }
          rules={fileUploadRules(isFileUploadRequired)}
          getValueFromEvent={normFile}
          getValueProps={(value) => ({
            fileList: mapUploadFileListForDisplay(value)
          })}
        >
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>Click to Upload</Button>
          </Upload>
        </Form.Item>
      );
    case ADD_MORE_FIELDS:
      // eslint-disable-next-line no-lone-blocks
      {
        field?.formList?.formFields.map(
          // eslint-disable-next-line array-callback-return
          (fieldItem) => {
            if (
              fieldItem.hasOwnProperty('fieldName') &&
              (fieldItem.type !== undefined ||
                fieldItem.type !== inputType.HEADING ||
                fieldItem.type !== inputType.HEADING_2 ||
                fieldItem.type !== inputType.HEADING_3)
            ) {
              blankObj[`${fieldItem.fieldName}`] = fieldItem.units
                ? {value: null, unit: ''}
                : '';
            }
          }
        );
      }

      return (
        <Form.Item shouldUpdate>
          {() => {
            return (
              <Form.List
                name={field?.formList?.name ?? ''}
                initialValue={[blankObj]}
              >
                {(fields, {add, remove}) => (
                  <>
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() => {
                          add();
                        }}
                        icon={<PlusOutlined />}
                        style={{width: 200}}
                        // onKeyDown={(e) => {
                        //   e.key === 'Enter' && add();
                        // }}
                      >
                        Add
                      </Button>
                    </Form.Item>
                    {fields.map(({key, name}) => (
                      <Space
                        key={key}
                        style={{display: 'block', marginBottom: 4}}
                        align="center"
                      >
                        {field?.formList?.formFields.map((formField, index) => {
                          const formFieldAny = formField as Record<string, any>;
                          const formFieldType =
                            formFieldAny.type ?? formFieldAny.fieldType;
                          const isFormListHeadingRow =
                            formFieldType === inputType.HEADING ||
                            formFieldType === inputType.HEADING_1 ||
                            formFieldType === inputType.HEADING_2 ||
                            formFieldType === inputType.HEADING_3 ||
                            formFieldType === inputType.HEADING_4 ||
                            formFieldType === inputType.HEADING_5;

                          return (
                            <QuestionnaireAddMoreFieldsSwitch
                              field={{
                                fieldType: formFieldType,
                                fieldLabel: replaceCurrentAndPrevious(
                                  formField.fieldLabel,
                                  selectedYear ?? ''
                                ),
                                questionNo: formFieldAny.questionNo,
                                placeholder: formField.placeHolder,
                                options: formField.options,
                                heading: isFormListHeadingRow
                                  ? replaceCurrentAndPrevious(
                                      formField.heading ?? '',
                                      selectedYear ?? ''
                                    )
                                  : formField.heading,
                                tooltip: formFieldAny.tooltip,
                                restrictFutureDateSelection:
                                  formField.restrictFutureDateSelection,
                                fieldName: [name, formField.fieldName],
                                units: formField.units,
                                dateFormat: formField.dateFormat,
                                relatedKey: formField.relatedKey,
                                index: name,
                                mainKey: field?.formList?.name,
                                colSpan: field.colSpan,
                                disabled: formFieldAny.disabled
                              }}
                              key={index}
                              getFieldValue={getFieldValue}
                            />
                          );
                        })}
                        {fields.length > 1 ? (
                          <MinusCircleOutlined
                            onClick={() => {
                              remove(name);
                            }}
                          />
                        ) : null}
                      </Space>
                    ))}
                  </>
                )}
              </Form.List>
            );
          }}
        </Form.Item>
      );
    case ADD_MORE_FIELDS_TOGGLE_TABLE:
      // eslint-disable-next-line no-lone-blocks
      {
        field?.formList?.formFields.map(
          // eslint-disable-next-line array-callback-return
          (fieldItem) => {
            if (
              fieldItem.hasOwnProperty('fieldName') &&
              (fieldItem.type !== undefined ||
                fieldItem.type !== inputType.HEADING ||
                fieldItem.type !== inputType.HEADING_2 ||
                fieldItem.type !== inputType.HEADING_3)
            ) {
              blankObj[`${fieldItem.fieldName}`] = fieldItem.units
                ? {value: null, unit: ''}
                : '';
            }
          }
        );
      }

      return (
        <Form.Item shouldUpdate noStyle>
          {({getFieldValue}) => {
            return getFieldValue(field.relatedKey) === field.triggerKey ? (
              // <Form.List
              //   name={field?.formList?.name ?? ''}
              //   initialValue={[blankObj]}
              // >
              //   {(fields, {add, remove}) => (
              //     <>
              //       {fields.map(({key, name}) => (
              //         <Space
              //           key={key}
              //           style={{display: 'flex', marginBottom: 1}}
              //           align="center"
              //         >
              //           {field?.formList?.formFields.map((formField, index) => {
              //             return (
              //               <QuestionnaireAddMoreFieldsSwitch
              //                 field={{
              //                   fieldType: formField.type,
              //                   fieldLabel: replaceCurrentAndPrevious(
              //                     formField.fieldLabel,
              //                     selectedYear ?? ''
              //                   ),
              //                   placeholder: formField.placeHolder,
              //                   options: formField.options,
              //                   heading: formField.heading,
              //                   restrictFutureDateSelection:
              //                     formField.restrictFutureDateSelection,
              //                   fieldName: [name, formField.fieldName],
              //                   units: formField.units,
              //                   dateFormat: formField.dateFormat,
              //                   relatedKey: formField.relatedKey,
              //                   index: name,
              //                   mainKey: field?.formList?.name,
              //                   colSpan: field.colSpan
              //                 }}
              //                 key={index}
              //                 getFieldValue={getFieldValue}
              //               />
              //             );
              //           })}
              //           {fields.length > 1 ? (
              //             <MinusCircleOutlined
              //               onClick={() => {
              //                 remove(name);
              //               }}
              //             />
              //           ) : null}
              //         </Space>
              //       ))}
              //       <Form.Item>
              //         <Button
              //           type="dashed"
              //           onClick={() => {
              //             add();
              //           }}
              //           block
              //           icon={<PlusOutlined />}
              //           style={{width: 200}}
              //           // onKeyDown={(e) => {
              //           //   e.key === 'Enter' && add();
              //           // }}
              //         >
              //           Add
              //         </Button>
              //       </Form.Item>
              //     </>
              //   )}
              // </Form.List>
              <Form.Item shouldUpdate name={field.fieldName}>
                <QuestionnaireTable
                  field={field}
                  getFieldValue={getFieldValue}
                />
              </Form.Item>
            ) : (
              <></>
            );
          }}
        </Form.Item>
      );

    case TABLE:
      return (
        <>
          <Table dataSource={dataSource}>
            {
              // eslint-disable-next-line array-callback-return
              field.tableStructure.map((tableField: any, tableIndex: any) => {
                if (tableField.hasOwnProperty('subLabels')) {
                  return (
                    <div key={tableIndex}>
                      <ColumnGroup title={tableField.label}>
                        {tableField.subLabels.map((columnGroupFields: any) => {
                          return (
                            <Column
                              title={columnGroupFields.label}
                              dataIndex={columnGroupFields.key}
                              key={columnGroupFields.key}
                            />
                          );
                        })}
                      </ColumnGroup>
                    </div>
                  );
                } else {
                  return (
                    <div key={tableIndex}>
                      <Column
                        title={tableField.label}
                        dataIndex={tableField.key}
                        key={tableField.key}
                      />
                    </div>
                  );
                }
              })
            }
          </Table>
          {field?.addMoreOption?.addMore ? (
            <Button onClick={handleAdd} type="primary">
              Add More
            </Button>
          ) : (
            ''
          )}
        </>
      );
    case ADD_NEW_TO_TABLE: {
      const addNewToTableRequiredOnDep = field.requiredOn?.fieldName;

      const renderAddNewToTableFormItem = (
        getFV: (name: any) => any
      ): React.ReactNode => {
        const isAddNewToTableRequired = isFieldRequired(field, getFV);

        const addNewToTableRules = isAddNewToTableRequired
          ? [
              {
                required: true,
                message: `${field.fieldLabel ?? 'This table'} is required`,
                validator: async (_: any, value: any) => {
                  if (!Array.isArray(value) || value.length === 0) {
                    return await Promise.reject(
                      new Error(
                        field.fieldLabel
                          ? `Add at least one row for ${field.fieldLabel}`
                          : 'Add at least one row'
                      )
                    );
                  }

                  await Promise.resolve();
                }
              }
            ]
          : [];

        return (
          <Form.Item
            name={field.fieldName}
            label={headerLabelContainer(
              field?.heading as any,
              displayFieldLabel
            )}
            tooltip={
              field.tooltip
                ? {
                    title: field.tooltip,
                    icon: <InfoCircleOutlined />
                  }
                : ''
            }
            className={field?.colSpan ? '' : 'col-span-center'}
            labelCol={field?.colSpan ? {span: 24} : undefined}
            rules={addNewToTableRules}
          >
            {field?.controlHeading &&
              headingComponent(inputType.HEADING, field?.controlHeading)}
            <QuestionnaireTable field={field} getFieldValue={getFV} />
          </Form.Item>
        );
      };

      const wrapAddNewToTableIfRequiredOn = (
        getFV: (name: any) => any
      ): React.ReactNode =>
        addNewToTableRequiredOnDep ? (
          <Form.Item
            shouldUpdate
            noStyle
            dependencies={[addNewToTableRequiredOnDep]}
          >
            {({getFieldValue: getFVD}) => renderAddNewToTableFormItem(getFVD)}
          </Form.Item>
        ) : (
          renderAddNewToTableFormItem(getFV)
        );

      return field && (field as any).dependsOn && (field as any).showWhen ? (
        <Form.Item
          shouldUpdate
          noStyle
          dependencies={[field.relatedTableKey || field.dependsOn]}
        >
          {({getFieldValue, setFieldValue}) => {
            const finalValue = getDependsOnValue(field, getFieldValue);

            // Handle both array and string values
            const showWhenArray = Array.isArray(field.showWhen)
              ? field.showWhen
              : [field.showWhen];
            let shouldShow = false;

            if (Array.isArray(finalValue)) {
              // If finalValue is an array (MULTISELECT), check if any value matches
              shouldShow = finalValue.some((val: any) =>
                showWhenArray.includes(val)
              );
            } else if (finalValue !== undefined && finalValue !== null) {
              // If finalValue is a string or single value, check if it's in showWhen
              shouldShow = showWhenArray.includes(finalValue);
            }

            if (!shouldShow) {
              const currentValue = getFieldValue(field.fieldName);
              if (
                currentValue !== undefined &&
                currentValue !== null &&
                currentValue !== ''
              ) {
                setFieldValue(field.fieldName, undefined);
              }
            }

            return shouldShow ? (
              wrapAddNewToTableIfRequiredOn(getFieldValue)
            ) : (
              <></>
            );
          }}
        </Form.Item>
      ) : (
        <Form.Item
          shouldUpdate
          dependencies={field?.conditions?.dependsOn}
          noStyle
        >
          {({getFieldValue}) => {
            let shouldShow = true;
            if (field?.conditions?.shouldShow) {
              shouldShow = evaluateExpression(
                field?.conditions?.shouldShow.expression,
                getFieldValue([])
              );
            }

            return shouldShow ? (
              wrapAddNewToTableIfRequiredOn(getFieldValue)
            ) : (
              <></>
            );
          }}
        </Form.Item>
      );
    }

    case ADD_FIXED_ROW_TO_TABLE: {
      const renderFixedRowTableFormItem = (
        getFV: (name: any) => any
      ): React.ReactNode => (
        <Form.Item
          shouldUpdate
          name={field.fieldName}
          label={headerLabelContainer(field?.heading as any, displayFieldLabel)}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />
                }
              : ''
          }
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
        >
          {field?.controlHeading &&
            headingComponent(inputType.HEADING, field?.controlHeading)}
          <QuestionnaireTableWithFixedRow field={field} getFieldValue={getFV} />
        </Form.Item>
      );

      return field && (field as any).dependsOn && (field as any).showWhen ? (
        <Form.Item
          shouldUpdate
          noStyle
          dependencies={[field.relatedTableKey || field.dependsOn]}
        >
          {({getFieldValue, setFieldValue}) => {
            const shouldShow = matchesShowWhen(
              field,
              getDependsOnValue(field, getFieldValue)
            );

            if (!shouldShow) {
              const currentValue = getFieldValue(field.fieldName);
              if (
                currentValue !== undefined &&
                currentValue !== null &&
                currentValue !== ''
              ) {
                setFieldValue(field.fieldName, undefined);
              }
            }

            return shouldShow ? (
              renderFixedRowTableFormItem(getFieldValue)
            ) : (
              <></>
            );
          }}
        </Form.Item>
      ) : (
        <Form.Item
          shouldUpdate
          dependencies={field?.conditions?.dependsOn}
          noStyle
        >
          {({getFieldValue}) => {
            let shouldShow = true;
            if (field?.conditions?.shouldShow) {
              shouldShow = evaluateExpression(
                field?.conditions?.shouldShow.expression,
                getFieldValue([])
              );
            }

            return shouldShow ? (
              renderFixedRowTableFormItem(getFieldValue)
            ) : (
              <></>
            );
          }}
        </Form.Item>
      );
    }
    case ADD_YEAR_FIXED_ROW_TO_TABLE: {
      const renderYearFixedRowTableFormItem = (
        getFV: (name: any) => any
      ): React.ReactNode => (
        <Form.Item
          shouldUpdate
          name={field.fieldName}
          label={headerLabelContainer(field?.heading as any, displayFieldLabel)}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />
                }
              : ''
          }
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
        >
          {field?.controlHeading &&
            headingComponent(inputType.HEADING, field?.controlHeading)}
          <QuestionnaireTableWithYearFixedRow
            field={field}
            getFieldValue={getFV}
          />
        </Form.Item>
      );

      return field && (field as any).dependsOn && (field as any).showWhen ? (
        <Form.Item
          shouldUpdate
          noStyle
          dependencies={[field.relatedTableKey || field.dependsOn]}
        >
          {({getFieldValue, setFieldValue}) => {
            const shouldShow = matchesShowWhen(
              field,
              getDependsOnValue(field, getFieldValue)
            );

            if (!shouldShow) {
              const currentValue = getFieldValue(field.fieldName);
              if (
                currentValue !== undefined &&
                currentValue !== null &&
                currentValue !== ''
              ) {
                setFieldValue(field.fieldName, undefined);
              }
            }

            return shouldShow ? (
              renderYearFixedRowTableFormItem(getFieldValue)
            ) : (
              <></>
            );
          }}
        </Form.Item>
      ) : (
        <Form.Item
          shouldUpdate
          dependencies={field?.conditions?.dependsOn}
          noStyle
        >
          {({getFieldValue}) => {
            let shouldShow = true;
            if (field?.conditions?.shouldShow) {
              shouldShow = evaluateExpression(
                field?.conditions?.shouldShow.expression,
                getFieldValue([])
              );
            }

            return shouldShow ? (
              renderYearFixedRowTableFormItem(getFieldValue)
            ) : (
              <></>
            );
          }}
        </Form.Item>
      );
    }
    case INPUT_GRID:
      return (
        <Form.Item shouldUpdate>
          <Row gutter={24}>
            <Col span={11}>
              <Row gutter={24}>
                {field?.formContent?.formFields.map((formField, index) => {
                  return (
                    <InputGrid
                      field={{
                        fieldType: formField.type,
                        fieldLabel: replaceCurrentAndPrevious(
                          formField.fieldLabel,
                          selectedYear ?? ''
                        ),
                        placeHolder: formField.placeHolder,
                        options: formField.options,
                        heading: replaceCurrentAndPrevious(
                          formField.heading,
                          selectedYear ?? ''
                        ),
                        restrictFutureDateSelection:
                          formField?.restrictFutureDateSelection,
                        fieldName: formField?.fieldName,
                        units: formField?.units,
                        dateFormat: formField?.dateFormat,
                        relatedKey: formField?.relatedKey,
                        tooltip: formField?.tooltip,
                        formList: formField?.formList,
                        colSpan: formField?.colSpan,
                        isFormatted: formField?.isFormatted,
                        askAIkey: formField?.askAIkey
                      }}
                      key={index}
                    />
                  );
                })}
              </Row>
            </Col>
            <Col span={13}>
              <Row
                style={{height: '100%', alignContent: 'space-evenly'}}
                justify="center"
                align="middle"
              >
                {field?.formContent?.formCalculations?.map(
                  (formField, index) => {
                    const formCalculations =
                      field?.formContent?.formCalculations || [];
                    const formCalculationsLength = formCalculations.length ?? 0;

                    return (
                      <DisplayCalculations
                        field={{
                          fieldType: formField.type,
                          fieldLabel: replaceCurrentAndPrevious(
                            formField.fieldLabel,
                            selectedYear ?? ''
                          ),
                          options: formField.options,
                          heading: replaceCurrentAndPrevious(
                            formField.heading,
                            selectedYear ?? ''
                          ),
                          restrictFutureDateSelection:
                            formField.restrictFutureDateSelection,
                          fieldName: formField.fieldName,
                          units: formField.units,
                          dateFormat: formField.dateFormat,
                          relatedKey: formField.relatedKey,
                          formula: formField?.formula,
                          roundTo: formField?.roundTo
                        }}
                        length={formCalculationsLength}
                        index={index}
                        key={index}
                      />
                    );
                  }
                )}
              </Row>
            </Col>
          </Row>
        </Form.Item>
      );

    case inputType.DISPLAY_CALCULATION_IN_TABLE:
      if (field?.relatedKey && field?.formula) {
        return (
          <Form.Item noStyle shouldUpdate>
            {() => (
              <DisplayCalculations
                field={{...field, fieldLabel: displayFieldLabel}}
                length={1}
                index={0}
                asInput
              />
            )}
          </Form.Item>
        );
      }

      return (
        <Form.Item
          name={field.fieldName}
          label={displayFieldLabel}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />
                }
              : ''
          }
        >
          <Input disabled placeholder="" />
        </Form.Item>
      );

    default:
      return (
        <Form.Item
          name={field.fieldName}
          label={displayFieldLabel}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />
                  // overlayStyle: tooltipCSS
                }
              : ''
          }
        >
          <Input
            disabled={field.disabled}
            name={field.fieldName}
            placeholder=""
          />
        </Form.Item>
      );
  }
};

export default QuestionnaireSwitch;
